import React, { useState, useRef } from 'react';
import { ArrowLeft, Camera, Image, FileText, Clock, ChevronRight, Upload, AlertCircle } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { STATUS_CONFIG, PRODUCTS, formatINR, OCR_TEMPLATE } from '@/data/mockData';

function LinkedProducts({ invoice }) {
  const linked = PRODUCTS.slice(0, 3);
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
      <div className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3">Linked Products</div>
      {linked.map((p, i) => (
        <div key={p.id} className={`flex justify-between items-center py-2 ${i < linked.length - 1 ? 'border-b border-slate-100' : ''}`}>
          <div>
            <div className="font-semibold text-slate-700 text-sm">{p.name}</div>
            <div className="text-xs text-slate-400">{p.pack}</div>
          </div>
          <div className="text-xs text-slate-400">{p.segment}</div>
        </div>
      ))}
    </div>
  );
}

export default function UploadInvoiceScreen() {
  const { goBack, navigate, screenParams } = useApp();
  const { invoice } = screenParams;
  const [scanning, setScanning] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileRef = useRef(null);

  if (!invoice) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-400">
        <AlertCircle size={40} className="mb-2" />
        <div>No invoice selected</div>
        <button onClick={goBack} className="mt-4 text-[#2563EB] font-semibold">Go Back</button>
      </div>
    );
  }

  const cfg = STATUS_CONFIG[invoice.status];

  const simulateUpload = (fileName) => {
    setSelectedFile(fileName);
    setScanning(true);
    setTimeout(() => {
      setScanning(false);
      navigate('OCR_REVIEW', {
        invoice,
        ocrData: {
          invoiceNo: invoice.invoiceNo,
          invoiceDate: invoice.date,
          buyerName: invoice.buyer,
          totalAmount: invoice.amount,
          lineItems: OCR_TEMPLATE.lineItems,
          remarks: '',
        },
      });
    }, 2200);
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) simulateUpload(file.name);
  };

  const uploadOptions = [
    { icon: Camera, label: 'Upload from Camera', sub: 'Take a photo of the invoice', testId: 'upload-camera-btn' },
    { icon: Image, label: 'Upload from Gallery', sub: 'Select from your gallery', testId: 'upload-gallery-btn' },
    { icon: FileText, label: 'Upload PDF', sub: 'Upload a PDF document', testId: 'upload-pdf-btn' },
  ];

  return (
    <div className="pb-6 relative">
      {/* Header */}
      <div className="bg-[#2563EB] px-4 pt-5 pb-5">
        <div className="flex items-center gap-3">
          <button
            data-testid="upload-back-btn"
            onClick={goBack}
            className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center tap-scale"
          >
            <ArrowLeft size={18} color="white" />
          </button>
          <div>
            <h1 className="text-white font-bold text-base">{invoice.invoiceNo}</h1>
            <div className="text-white/70 text-xs">{invoice.date} · {invoice.buyer}</div>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4 flex flex-col gap-4">
        {/* Invoice summary */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div className="flex justify-between items-center mb-3">
            <div className="text-xs font-black text-slate-500 uppercase tracking-widest">Invoice Details</div>
            <span className={`text-[10px] font-bold px-2 py-1 rounded-lg ${cfg.className}`}>{cfg.label}</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Invoice No</div>
              <div className="font-semibold text-slate-700 text-sm mt-0.5">{invoice.invoiceNo}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Invoice Date</div>
              <div className="font-semibold text-slate-700 text-sm mt-0.5">{invoice.date}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Buyer Name</div>
              <div className="font-semibold text-slate-700 text-sm mt-0.5">{invoice.buyer}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Sale Value</div>
              <div className="rupee-value font-bold text-[#1E2744] mt-0.5">₹{formatINR(invoice.amount)}</div>
            </div>
          </div>
        </div>

        {/* Upload area */}
        {!scanning ? (
          <>
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
              <div className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3">Upload Invoice</div>
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-5 mb-4 text-center bg-slate-50">
                <Upload size={32} color="#94A3B8" className="mx-auto mb-2" />
                <div className="text-slate-600 font-semibold text-sm">Drag & drop or choose file</div>
                <div className="text-slate-400 text-xs mt-1">Upload a clear invoice image or PDF for faster verification</div>
              </div>
              {uploadOptions.map(({ icon: Icon, label, sub, testId }) => (
                <button
                  key={testId}
                  data-testid={testId}
                  onClick={() => {
                    fileRef.current?.click();
                    // Also directly simulate if no file API
                    setTimeout(() => {
                      if (!selectedFile) simulateUpload('invoice_scan.jpg');
                    }, 100);
                  }}
                  className="w-full flex items-center gap-3 py-3 border-b border-slate-100 last:border-0 tap-scale text-left"
                >
                  <div className="w-10 h-10 bg-[#EFF6FF] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon size={18} color="#2563EB" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-slate-700 text-sm">{label}</div>
                    <div className="text-xs text-slate-400">{sub}</div>
                  </div>
                  <ChevronRight size={16} color="#94A3B8" />
                </button>
              ))}
              <input ref={fileRef} type="file" className="hidden" accept="image/*,.pdf" onChange={handleFileInput} />
            </div>

            <button
              data-testid="mark-for-later-btn"
              onClick={goBack}
              className="w-full py-3.5 border-2 border-slate-200 rounded-xl font-semibold text-slate-500 text-sm tap-scale flex items-center justify-center gap-2"
            >
              <Clock size={16} />
              Mark for Later
            </button>
          </>
        ) : (
          /* Scanning state */
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 flex flex-col items-center">
            <div className="relative w-20 h-20 mb-4">
              <div className="w-20 h-20 rounded-full border-4 border-[#2563EB]/20 absolute pulse-ring" />
              <div className="w-20 h-20 rounded-full border-4 border-[#2563EB] border-t-transparent animate-spin" />
              <div className="absolute inset-0 flex items-center justify-center">
                <FileText size={24} color="#2563EB" />
              </div>
            </div>
            <div className="font-bold text-[#1E2744] text-base mb-1">Scanning Invoice...</div>
            <div className="text-sm text-slate-400 text-center">OCR is reading your document.<br />This will take a moment.</div>
            {selectedFile && (
              <div className="mt-4 bg-slate-50 rounded-xl px-4 py-2">
                <div className="text-xs text-slate-500 font-medium">{selectedFile}</div>
              </div>
            )}
          </div>
        )}

        <LinkedProducts invoice={invoice} />
      </div>
    </div>
  );
}
